import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchCampaignOutboundBatches,
  fetchOutboundBatchDetail,
  type WhatsAppOutboundBatchDetail,
  type WhatsAppOutboundBatchSummary,
} from '../../services/MasterAdmin/whatsappCampaignService';
import { EmptyState, StatCard, StatusBadge, formatDateTime } from './whatsappCampaignUi';

type Props = {
  campaignId: string;
};

function batchPhaseLabel(batch: WhatsAppOutboundBatchSummary) {
  const delayMin = Math.round((batch.waveDelaySeconds || 0) / 60);
  if (delayMin >= 60) {
    const hours = Math.round(delayMin / 60);
    return `${hours}h between phases · ${batch.batchSize}/phase`;
  }
  return delayMin > 0 ? `${delayMin} min between phases · ${batch.batchSize}/phase` : `${batch.batchSize}/phase`;
}

const WhatsAppCampaignBatchesTab: React.FC<Props> = ({ campaignId }) => {
  const [batches, setBatches] = useState<WhatsAppOutboundBatchSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<WhatsAppOutboundBatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadBatches = useCallback(async () => {
    if (!campaignId) return;
    const res = await fetchCampaignOutboundBatches(campaignId, { limit: 30 });
    const list = res.data || [];
    setBatches(list);
    setSelectedId((prev) => {
      if (prev && list.some((b) => b.id === prev)) return prev;
      const active = list.find((b) => b.status === 'PENDING' || b.status === 'RUNNING');
      return active?.id || list[0]?.id || null;
    });
    setLoading(false);
  }, [campaignId]);

  const loadDetail = useCallback(
    async (batchId: string) => {
      setDetailLoading(true);
      try {
        const res = await fetchOutboundBatchDetail(batchId);
        setDetail(res.data);
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    void loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const hasActiveBatch = useMemo(
    () => batches.some((b) => b.status === 'PENDING' || b.status === 'RUNNING'),
    [batches]
  );

  useEffect(() => {
    if (!hasActiveBatch) return;
    const timer = window.setInterval(() => {
      void loadBatches();
      if (selectedId) void loadDetail(selectedId);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [hasActiveBatch, loadBatches, loadDetail, selectedId]);

  const activeBatches = batches.filter((b) => b.status === 'PENDING' || b.status === 'RUNNING');
  const completedBatches = batches.filter((b) => !['PENDING', 'RUNNING'].includes(String(b.status || '')));

  if (loading && !batches.length) {
    return <EmptyState title="Loading batches…" description="Fetching outbound send waves for this campaign." />;
  }

  if (!batches.length) {
    return (
      <EmptyState
        title="No outbound batches yet"
        description="Large sends (250+ contacts) are queued in phased batches. Start a send from the campaign editor to create the first batch."
      />
    );
  }

  const selected = batches.find((b) => b.id === selectedId) || null;

  return (
    <div className="space-y-6">
      {activeBatches.length > 0 && (
        <section>
          <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-amber-500" />
            Ongoing ({activeBatches.length})
          </h4>
          <div className="space-y-2">
            {activeBatches.map((batch) => (
              <BatchRow
                key={batch.id}
                batch={batch}
                selected={batch.id === selectedId}
                onSelect={() => setSelectedId(batch.id)}
              />
            ))}
          </div>
        </section>
      )}

      {completedBatches.length > 0 && (
        <section>
          <h4 className="mb-3 text-sm font-semibold text-gray-600 dark:text-gray-300">
            Completed ({completedBatches.length})
          </h4>
          <div className="space-y-2">
            {completedBatches.map((batch) => (
              <BatchRow
                key={batch.id}
                batch={batch}
                selected={batch.id === selectedId}
                onSelect={() => setSelectedId(batch.id)}
              />
            ))}
          </div>
        </section>
      )}

      {selected && (
        <section className="rounded-xl border border-[#009de2]/30 bg-[#009de2]/5 p-4 dark:border-[#009de2]/20">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="font-semibold">Batch detail</h4>
              <p className="text-xs text-gray-500">{batchPhaseLabel(selected)}</p>
            </div>
            <StatusBadge value={selected.status} />
          </div>

          <div className="mb-4 h-2.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-[#009de2] transition-all"
              style={{ width: `${selected.progressPct || 0}%` }}
            />
          </div>
          <p className="mb-3 text-xs text-gray-600 dark:text-gray-400">
            {selected.progressPct}% · {selected.totalAttempted}/{selected.totalQueued} attempted
            {selected.remaining > 0 ? ` · ${selected.remaining} remaining` : ''}
            {selected.nextRunAt ? ` · Next wave ${formatDateTime(selected.nextRunAt)}` : ''}
          </p>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Queued" value={selected.totalQueued} />
            <StatCard label="Sent" value={selected.totalSent} />
            <StatCard label="Failed" value={selected.totalFailed} />
            <StatCard label="Skipped" value={selected.totalSkipped} />
          </div>

          {detailLoading && !detail ? (
            <p className="mt-4 text-xs text-gray-500">Loading wave breakdown…</p>
          ) : detail?.waves?.length ? (
            <div className="mt-4 overflow-x-auto rounded-lg border bg-white dark:border-gray-700 dark:bg-gray-900">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-[10px] uppercase dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left">Phase</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Attempted</th>
                    <th className="px-3 py-2 text-right">Sent</th>
                    <th className="px-3 py-2 text-right">Failed</th>
                    <th className="px-3 py-2 text-left">Finished</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.waves.map((wave) => (
                    <tr key={wave.id} className="border-t dark:border-gray-800">
                      <td className="px-3 py-2 font-medium">Phase {wave.waveIndex + 1}</td>
                      <td className="px-3 py-2">
                        <StatusBadge value={wave.status} />
                      </td>
                      <td className="px-3 py-2 text-right">{wave.attempted}</td>
                      <td className="px-3 py-2 text-right">{wave.sent}</td>
                      <td className="px-3 py-2 text-right">{wave.failed}</td>
                      <td className="px-3 py-2 text-gray-500">{formatDateTime(wave.finishedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {detail?.itemStatusCounts && Object.keys(detail.itemStatusCounts).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(detail.itemStatusCounts).map(([status, count]) => (
                <span
                  key={status}
                  className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] dark:border-gray-700 dark:bg-gray-900"
                >
                  {status}: <strong>{count}</strong>
                </span>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

function BatchRow({
  batch,
  selected,
  onSelect,
}: {
  batch: WhatsAppOutboundBatchSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  const isActive = batch.status === 'PENDING' || batch.status === 'RUNNING';
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-xl border px-4 py-3 text-left transition ${
        selected
          ? 'border-[#009de2] bg-[#009de2]/10 ring-1 ring-[#009de2]/30'
          : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {isActive ? (
            <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-amber-500" />
          ) : (
            <span className="inline-flex h-2 w-2 rounded-full bg-green-500" />
          )}
          <span className="text-sm font-medium">
            {batch.reason || 'Campaign send'} · {batchPhaseLabel(batch)}
          </span>
        </div>
        <StatusBadge value={batch.status} />
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className={`h-full rounded-full ${isActive ? 'bg-amber-500' : 'bg-green-500'}`}
          style={{ width: `${batch.progressPct || 0}%` }}
        />
      </div>
      <p className="mt-1.5 text-[11px] text-gray-500">
        {batch.progressPct}% · {batch.totalAttempted}/{batch.totalQueued} · sent {batch.totalSent}
        {batch.startedAt ? ` · started ${formatDateTime(batch.startedAt)}` : ''}
        {batch.completedAt ? ` · done ${formatDateTime(batch.completedAt)}` : ''}
      </p>
    </button>
  );
}

export default WhatsAppCampaignBatchesTab;
